import React, { useMemo } from 'react';
import { Card, TextField, ToggleSwitchField } from '@dopomogai/ui';
import { McpServerItem, McpServerType } from '@dopomogai/types';
import HeaderEditor from './HeaderEditor';

export interface McpServerFormProps {
  server: McpServerItem;
  onChange: (next: McpServerItem) => void;
}

export const McpServerForm: React.FC<McpServerFormProps> = ({ server, onChange }) => {
  const isHttp = server.type === 'http';
  const isSse = server.type === 'sse';
  const isStdio = server.type === 'stdio';

  const onField = (field: keyof McpServerItem, value: any) => onChange({ ...server, [field]: value });

  const onHeaders = (next: Record<string, string>) => onField('headers_template', next);

  const urlHelp = isHttp ? 'Base URL returning a tool list over HTTP.' : isSse ? 'SSE endpoint (text/event-stream).' : '';

  return (
    <Card className="bg-slate-50 border border-dashed" noPadding={false}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(isHttp || isSse) && (
          <TextField
            label="URL"
            value={server.url || ''}
            onChange={(e) => onField('url', e.target.value)}
            placeholder="https://api.example.com/tools"
            descriptionText={urlHelp}
          />
        )}
        {isStdio && (
          <>
            <TextField
              label="Command"
              value={server.command || ''}
              onChange={(e) => onField('command', e.target.value)}
              placeholder="my_mcp_server"
            />
            <TextField
              label="Args (space separated)"
              value={(server.args || []).join(' ')}
              onChange={(e) => onField('args', e.target.value.split(/\s+/).filter(Boolean))}
              placeholder="--flag value"
            />
          </>
        )}

        <TextField
          label="Tool prefix"
          value={server.tool_prefix || ''}
          onChange={(e) => onField('tool_prefix', e.target.value)}
          placeholder="ext_http"
          descriptionText="Optional prefix used to namespace tools from this server."
        />
        <TextField
          label="Timeout (seconds)"
          value={server.timeout_sec != null ? String(server.timeout_sec) : ''}
          onChange={(e) => onField('timeout_sec', e.target.value ? Number(e.target.value) : null)}
          placeholder="10"
        />

        {(isHttp || isSse) && (
          <ToggleSwitchField
            label="Verify TLS"
            checked={!!server.verify_tls}
            onChange={(e) => onField('verify_tls', e.target.checked)}
          />
        )}
        <ToggleSwitchField
          label="Allow sampling"
          checked={!!server.allow_sampling}
          onChange={(e) => onField('allow_sampling', e.target.checked)}
        />
      </div>

      <div className="mt-4">
        <HeaderEditor headers={server.headers_template || {}} onChange={onHeaders} />
      </div>
    </Card>
  );
};

export default McpServerForm;
